/**
 * KnowledgeView v2 -- Gestion unificada de instrucciones + RAG.
 * Tabla NxListViewer con columnas: Codigo, Nombre, V.BD, V.RAG, Estado, Accion, Ingesta.
 * Split detail: preview de instruccion + metadata RAG.
 *
 * v1 → v2: Reemplaza 2 listas separadas por tabla unificada con tracking de version RAG.
 * Bug fix: v1 mostraba version BD como version RAG (linea 50 original).
 *
 * @module presentation/views/KnowledgeView
 */

import { RagApiClient } from '../../infrastructure/api/RagApiClient.js';
import { InstructionApiClient } from '../../infrastructure/api/InstructionApiClient.js';
import { openDocumentModal, buildInstructionContent } from '../shared/DocumentViewer.js';
import { renderNxList, bindNxListEvents } from '../shared/NxListViewer.js';
import { avisar, confirmar } from '../shared/nxDialogo.js';

const ADA002_CUTOFF = '2026-04-03T00:00:00Z';

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

function fmtDate(iso) {
  if (!iso) return '—';
  return formatearFecha(iso, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Status logic ──

/**
 * Compara BD vs RAG para determinar estado de sincronizacion.
 * Prioriza rag_version (tracking real); fallback a comparacion de titulos.
 */
function computeStatus(inst, ragDoc) {
  if (!ragDoc) return 'missing';

  const ragVer = inst.ragVersion;
  const bdVer = inst.instructionVersion;

  // Version tracking disponible (despues de PATCH rag-status)
  if (ragVer && bdVer) {
    return ragVer === bdVer ? 'synced' : 'stale';
  }

  // Fallback: comparar titulos (legacy, antes de rag_version tracking)
  const ragTitle = (ragDoc.title || '').replace(/^[A-Z0-9-]+ - /, '').trim();
  const bdTitle = (inst.title || '').trim();
  if (ragTitle && bdTitle && ragTitle !== bdTitle) return 'stale';

  // En RAG pero sin version tracking
  return 'in_rag';
}

function statusBadge(status) {
  switch (status) {
    case 'synced':  return '<span class="kg-badge kg-badge--current">Sincronizado</span>';
    case 'stale':   return '<span class="kg-badge kg-badge--old">Desactualizado</span>';
    case 'missing': return '<span class="kg-badge kg-badge--missing">No ingestado</span>';
    case 'in_rag':  return '<span class="kg-badge kg-badge--collection">En RAG</span>';
    default:        return '<span class="kg-badge kg-badge--missing">?</span>';
  }
}

function modelBadge(ragDoc) {
  if (!ragDoc) return '—';
  if (ragDoc.model) {
    const is3small = ragDoc.model.includes('3-small');
    if (is3small) return '<span class="kg-badge kg-badge--current" title="text-embedding-3-small">3-small</span>';
    return `<span class="kg-badge kg-badge--old" title="${esc(ragDoc.model)}">${esc(ragDoc.model)}</span>`;
  }
  const isOld = !ragDoc.created_at || ragDoc.created_at < ADA002_CUTOFF;
  return isOld
    ? '<span class="kg-badge kg-badge--old" title="ada-002">ada-002</span>'
    : '<span class="kg-badge kg-badge--current" title="3-small">3-small</span>';
}

function actionButton(row) {
  const label = row.status === 'missing' ? 'Ingestar'
    : row.status === 'stale' ? 'Actualizar'
    : 'Re-ingestar';
  return `<button class="btn-ghost btn-sm kg-btn-action"
    data-action="ingest" data-id="${esc(row.id)}"
    data-code="${esc(row.code)}" data-title="${esc(row.title)}">${label}</button>`;
}

// ── State ──
let ragDocs = [];
let instructions = [];
let ragDocIds = new Set();
let tableRows = [];
let _lastOrphans = [];
let _listConfig = null;
let _archivedRows = [];

// ── Entry point ──

export async function renderKnowledge(container) {
  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Conocimiento RAG</h1>
          <p class="subtitle">Sincronizacion BD &harr; RAG (pgvector) &middot; collection <code>public</code></p>
        </div>
      </div>

      <div id="kg-stats" class="kg-stats"></div>

      <div style="margin-bottom:0.75rem;display:flex;gap:0.5rem;align-items:center;flex-wrap:wrap">
        <button class="btn-primary btn-sm" id="kg-btn-ingest-all">Ingestar todas</button>
        <button class="btn-ghost btn-sm" id="kg-btn-clean-all" style="color:var(--accent-red)"
          title="Eliminar vectores de instrucciones archivadas y documentos huerfanos">Limpiar obsoletos</button>
        <button class="btn-ghost btn-sm" id="kg-btn-refresh">Refrescar</button>
      </div>

      <div id="kg-progress" hidden class="kg-progress">
        <div class="kg-progress-bar"><div class="kg-progress-fill" id="kg-progress-fill"></div></div>
        <span id="kg-progress-text" class="kg-progress-text"></span>
      </div>

      <div id="kg-table-container">
        <p class="empty-state">Cargando...</p>
      </div>

      <div id="kg-archived" hidden style="margin-top:1.5rem">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem">
          <h3 class="kg-section-title" style="margin:0">Instrucciones archivadas en BD</h3>
          <button class="btn-ghost btn-sm" id="kg-btn-clean-archived"
            style="color:var(--accent-red)">Limpiar vectores RAG</button>
        </div>
        <p style="font-size:0.875rem;color:var(--text-secondary);margin:0 0 0.5rem">
          Estas instrucciones fueron absorbidas o desactivadas. Sus vectores en RAG son obsoletos.
        </p>
        <div id="kg-archived-list" class="artifact-list"></div>
      </div>

      <div id="kg-orphans" hidden style="margin-top:1.5rem">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem">
          <h3 class="kg-section-title" style="margin:0">Documentos RAG sin instruccion en BD</h3>
          <button class="btn-ghost btn-sm" id="kg-btn-clean-orphans"
            style="color:var(--accent-red)">Eliminar todos</button>
        </div>
        <div id="kg-orphan-list" class="artifact-list"></div>
      </div>
    </section>`;

  setupHandlers(container);
  await loadAll(container);
}

// ── Event handlers ──

function setupHandlers(container) {
  container.addEventListener('click', (e) => {
    const actionBtn = e.target.closest('.kg-btn-action');
    if (actionBtn) {
      e.stopPropagation();
      const { id, code, title } = actionBtn.dataset;
      handleIngestSingle(id, code, title, container);
      return;
    }

    const deleteBtn = e.target.closest('.kg-btn-delete');
    if (deleteBtn) {
      e.stopPropagation();
      handleDelete(deleteBtn.dataset.docId, Number(deleteBtn.dataset.chunks), container);
      return;
    }
  });

  document.getElementById('kg-btn-ingest-all').onclick = () => handleIngestAll(container);
  document.getElementById('kg-btn-clean-all').onclick = () => handleCleanAll(container);
  document.getElementById('kg-btn-refresh').onclick = () => loadAll(container);
  document.getElementById('kg-btn-clean-archived')?.addEventListener('click', () => handleCleanArchived(container));
  document.getElementById('kg-btn-clean-orphans')?.addEventListener('click', () => handleCleanOrphans(container));
}

// ── Data loading ──

async function loadAll(container) {
  await Promise.all([loadDocuments(), loadInstructions()]);
  buildTableRows();
  renderTable(container);
  renderStats();
  renderArchived();
  renderOrphans();
}

async function loadDocuments() {
  const result = await RagApiClient.listDocuments();
  if (!result.ok) { ragDocs = []; ragDocIds = new Set(); return; }

  const data = result.data?.documents || result.data || [];
  ragDocs = Array.isArray(data) ? data : [];
  ragDocIds = new Set(ragDocs.map(d => d.doc_id));
  _lastOrphans = result.data?.orphans || [];
}

async function loadInstructions() {
  const result = await InstructionApiClient.list(1, 100);
  if (result.ok) {
    const payload = result.data?.data || result.data;
    instructions = payload?.items || [];
  } else {
    instructions = [];
  }
}

// ── Table rendering ──

function _mapRow(inst) {
  const ragDoc = ragDocs.find(d => d.doc_id === inst.code);
  const status = computeStatus(inst, ragDoc);
  return {
    id: inst.id,
    code: inst.code,
    title: inst.title,
    area: inst.area || '',
    vBd: inst.instructionVersion || '?',
    vRag: inst.ragVersion || null,
    ragIngestedAt: inst.ragIngestedAt || ragDoc?.created_at || null,
    status,
    stepCount: inst.stepCount || 0,
    ragDoc,
    _inst: inst
  };
}

function buildTableRows() {
  const active = instructions.filter(i => i.status === 'active');
  const archived = instructions.filter(i => i.status !== 'active');
  tableRows = active.map(_mapRow);
  _archivedRows = archived.map(_mapRow);
}

function renderTable(container) {
  const $container = document.getElementById('kg-table-container');
  if (!$container) return;

  _listConfig = {
    id: 'kg-unified-list',
    columns: [
      {
        key: 'code', label: 'Codigo', width: '145px',
        render: (v) => `<code class="instruction-code">${esc(v)}</code>`
      },
      { key: 'title', label: 'Nombre' },
      {
        key: 'vBd', label: 'V.BD', width: '60px',
        render: (v) => `<span title="Version en base de datos">${esc(v)}</span>`
      },
      {
        key: 'vRag', label: 'V.RAG', width: '65px',
        render: (v, row) => {
          if (!row.ragDoc) return '<span style="color:var(--text-muted)">—</span>';
          if (v) return `<span title="Version ingestada en RAG">${esc(v)}</span>`;
          return '<span style="color:var(--text-muted)" title="Sin tracking de version">?</span>';
        }
      },
      {
        key: 'status', label: 'Estado', width: '125px',
        render: (v) => statusBadge(v)
      },
      {
        key: 'action', label: '', width: '105px',
        render: (_, row) => actionButton(row)
      },
      {
        key: 'ragIngestedAt', label: 'Ingesta', width: '105px',
        render: (v) => `<span style="font-size:0.875rem">${fmtDate(v)}</span>`
      }
    ],
    data: tableRows,
    sortBy: 'code',
    sortDir: 'asc',
    emptyMessage: 'No hay instrucciones en BD',
    filterPlaceholder: 'Filtrar instrucciones...'
  };

  $container.innerHTML = renderNxList(_listConfig);

  bindNxListEvents(container, 'kg-unified-list', _listConfig, null, (rowData, detailEl) => {
    renderDetailPanel(rowData, detailEl);
  });
}

function renderDetailPanel(row, el) {
  const ragInfo = row.ragDoc
    ? `<div style="margin-top:0.75rem;font-size:0.875rem;color:var(--text-secondary)">
        <strong>RAG:</strong> ${row.ragDoc.chunk_count} chunk${row.ragDoc.chunk_count !== 1 ? 's' : ''} &middot; ${modelBadge(row.ragDoc)}
        <br>Ingesta: ${fmtDate(row.ragDoc.created_at)}
        ${row.ragDoc.collection ? `<br>Collection: <code>${esc(row.ragDoc.collection)}</code>` : ''}
      </div>`
    : '<p style="color:var(--text-muted);font-size:0.875rem;margin-top:0.5rem">No ingestado en RAG</p>';

  el.innerHTML = `
    <div style="padding:0.5rem">
      <h4 style="margin:0 0 0.25rem 0;color:var(--text-heading)">${esc(row.code)}</h4>
      <p style="margin:0 0 0.5rem 0;font-size:0.875rem;color:var(--text-secondary)">${esc(row.title)}</p>
      <div style="display:flex;gap:0.5rem;flex-wrap:wrap;margin-bottom:0.5rem">
        ${statusBadge(row.status)}
        <span class="kg-badge" style="background:var(--bg-tertiary);color:var(--text-heading)">${row.stepCount} pasos</span>
        ${row.area ? `<span class="kg-badge kg-badge--collection">${esc(row.area)}</span>` : ''}
      </div>
      <div style="font-size:0.875rem;color:var(--text-secondary);margin-bottom:0.5rem">
        V.BD: <strong>${esc(row.vBd)}</strong>
        ${row.ragDoc ? ` &middot; V.RAG: <strong>${esc(row.vRag || '?')}</strong>` : ''}
      </div>
      ${ragInfo}
      <div style="margin-top:0.75rem;display:flex;gap:0.5rem;flex-wrap:wrap">
        <button class="btn-ghost btn-sm kg-detail-view">Ver contenido</button>
        ${row.ragDoc
          ? `<button class="btn-ghost btn-sm kg-btn-delete"
              data-doc-id="${esc(row.code)}" data-chunks="${row.ragDoc.chunk_count}"
              style="color:var(--accent-red)">Eliminar RAG</button>`
          : ''}
      </div>
    </div>`;

  // Bind detail view button
  el.querySelector('.kg-detail-view')?.addEventListener('click', () => {
    handleViewDocument(row.id, row.code, row.title);
  });
}

function renderStats() {
  const $stats = document.getElementById('kg-stats');
  if (!$stats) return;

  const activeCount = tableRows.length;
  const archivedCount = _archivedRows.length;
  const inRag = tableRows.filter(r => r.ragDoc).length;
  const synced = tableRows.filter(r => r.status === 'synced').length;
  const stale = tableRows.filter(r => r.status === 'stale').length;
  const missing = tableRows.filter(r => r.status === 'missing').length;
  const totalChunks = ragDocs.reduce((s, d) => s + Number(d.chunk_count || 0), 0);
  const orphanTotal = _lastOrphans.reduce((s, o) => s + Number(o.orphan_count || 0), 0);
  const archivedInRag = _archivedRows.filter(r => r.ragDoc).length;
  const instructionCodes = new Set(instructions.map(i => i.code));
  const orphanDocs = ragDocs.filter(d => !instructionCodes.has(d.doc_id)).length;

  $stats.innerHTML = `
    <span>${activeCount} activas</span>
    <span>&middot; ${inRag} en RAG</span>
    <span>&middot; ${totalChunks} chunks</span>
    ${synced > 0 ? `<span style="color:var(--accent-green)">&middot; ${synced} sincronizadas</span>` : ''}
    ${stale > 0 ? `<span class="kg-stats-warn">&middot; ${stale} desactualizadas</span>` : ''}
    ${missing > 0 ? `<span style="color:var(--text-muted)">&middot; ${missing} sin ingestar</span>` : ''}
    ${archivedInRag > 0 ? `<span class="kg-stats-warn">&middot; ${archivedInRag} archivadas en RAG</span>` : ''}
    ${orphanDocs > 0 ? `<span class="kg-stats-warn">&middot; ${orphanDocs} docs huerfanos</span>` : ''}
    ${orphanTotal > 0 ? `<span class="kg-stats-warn">&middot; ${orphanTotal} vectores sueltos</span>` : ''}
  `;
}

function renderOrphans() {
  const instructionCodes = new Set(instructions.map(i => i.code));
  const orphanDocs = ragDocs.filter(d => !instructionCodes.has(d.doc_id));

  const $section = document.getElementById('kg-orphans');
  const $list = document.getElementById('kg-orphan-list');
  if (!$section || !$list) return;

  if (orphanDocs.length === 0) { $section.hidden = true; return; }

  $section.hidden = false;
  $list.innerHTML = orphanDocs.map(doc => `
    <div class="artifact-item" style="cursor:default">
      <div class="task-info">
        <div class="artifact-item-title">
          <code class="instruction-code">${esc(doc.doc_id)}</code> ${esc(doc.title || '(sin titulo)')}
        </div>
        <div class="artifact-item-meta">
          ${doc.chunk_count} chunks &middot; ${fmtDate(doc.created_at)} &middot; ${modelBadge(doc)}
        </div>
      </div>
      <div class="task-actions">
        <button class="btn-icon kg-btn-delete" data-doc-id="${esc(doc.doc_id)}"
                data-chunks="${doc.chunk_count}" title="Eliminar del vector store">&#128465;</button>
      </div>
    </div>`).join('');
}

// ── Archived instructions ──

function renderArchived() {
  const $section = document.getElementById('kg-archived');
  const $list = document.getElementById('kg-archived-list');
  if (!$section || !$list) return;

  if (_archivedRows.length === 0) { $section.hidden = true; return; }

  $section.hidden = false;
  $list.innerHTML = _archivedRows.map(row => {
    const hasRag = row.ragDoc;
    const ragInfo = hasRag
      ? `${row.ragDoc.chunk_count} chunks &middot; ${fmtDate(row.ragDoc.created_at)}`
      : 'Sin vectores en RAG';
    const ragColor = hasRag ? 'var(--accent-yellow)' : 'var(--accent-green)';
    return `
      <div class="artifact-item" style="cursor:default;opacity:0.75">
        <div class="task-info">
          <div class="artifact-item-title">
            <code class="instruction-code">${esc(row.code)}</code> ${esc(row.title)}
          </div>
          <div class="artifact-item-meta">
            V.BD: ${esc(row.vBd)} &middot;
            <span style="color:${ragColor}">${ragInfo}</span>
            &middot; Estado BD: <span style="color:var(--accent-red)">inactiva</span>
          </div>
        </div>
        <div class="task-actions">
          ${hasRag
            ? `<button class="btn-ghost btn-sm kg-btn-delete"
                data-doc-id="${esc(row.code)}" data-chunks="${row.ragDoc.chunk_count}"
                style="color:var(--accent-red)" title="Eliminar vectores de RAG">Limpiar RAG</button>`
            : '<span style="color:var(--accent-green);font-size:0.875rem">✓ Limpio</span>'}
        </div>
      </div>`;
  }).join('');
}

// ── Cleanup functions ──

async function handleCleanArchived(container) {
  const withRag = _archivedRows.filter(r => r.ragDoc);
  if (withRag.length === 0) { avisar('No hay vectores de instrucciones archivadas para limpiar.'); return; }
  if (!await confirmar({
    titulo: `Eliminar vectores de ${withRag.length} instrucciones archivadas`,
    texto: withRag.map(r => r.code).join('\n'),
    aceptar: 'Eliminar vectores',
    peligro: true
  })) return;

  showProgress(true);
  let ok = 0;
  for (let i = 0; i < withRag.length; i++) {
    updateProgress(i, withRag.length, `Eliminando ${withRag[i].code}...`);
    const res = await RagApiClient.deleteDocument(withRag[i].code);
    if (res.ok) ok++;
  }
  updateProgress(withRag.length, withRag.length, `Listo: ${ok}/${withRag.length} eliminados`);
  await loadAll(container);
  setTimeout(() => showProgress(false), 3000);
}

async function handleCleanOrphans(container) {
  const instructionCodes = new Set(instructions.map(i => i.code));
  const orphanDocs = ragDocs.filter(d => !instructionCodes.has(d.doc_id));
  if (orphanDocs.length === 0) { avisar('No hay documentos huerfanos en RAG.'); return; }
  if (!await confirmar({
    titulo: `Eliminar ${orphanDocs.length} documentos huerfanos del RAG`,
    texto: orphanDocs.map(d => d.doc_id).join('\n'),
    aceptar: 'Eliminar documentos',
    peligro: true
  })) return;

  showProgress(true);
  let ok = 0;
  for (let i = 0; i < orphanDocs.length; i++) {
    updateProgress(i, orphanDocs.length, `Eliminando ${orphanDocs[i].doc_id}...`);
    const res = await RagApiClient.deleteDocument(orphanDocs[i].doc_id);
    if (res.ok) ok++;
  }
  updateProgress(orphanDocs.length, orphanDocs.length, `Listo: ${ok}/${orphanDocs.length} eliminados`);
  await loadAll(container);
  setTimeout(() => showProgress(false), 3000);
}

async function handleCleanAll(container) {
  const archivedWithRag = _archivedRows.filter(r => r.ragDoc);
  const instructionCodes = new Set(instructions.map(i => i.code));
  const orphanDocs = ragDocs.filter(d => !instructionCodes.has(d.doc_id));
  const orphanVectors = _lastOrphans.reduce((s, o) => s + Number(o.orphan_count || 0), 0);

  if (archivedWithRag.length === 0 && orphanDocs.length === 0 && orphanVectors === 0) {
    avisar('No hay vectores obsoletos para limpiar.'); return;
  }

  const lines = [];
  if (archivedWithRag.length > 0) lines.push(`• ${archivedWithRag.length} instrucciones archivadas con vectores`);
  if (orphanDocs.length > 0) lines.push(`• ${orphanDocs.length} documentos huerfanos en RAG`);
  if (orphanVectors > 0) lines.push(`• ${orphanVectors} vectores sueltos (sin metadata.fileName)`);
  if (!await confirmar({
    titulo: 'Limpiar vectores obsoletos',
    texto: lines.join('\n'),
    aceptar: 'Limpiar',
    peligro: true
  })) return;

  showProgress(true);
  let ok = 0;

  // Paso 1: Eliminar docs de instrucciones archivadas
  const toDelete = [
    ...archivedWithRag.map(r => r.code),
    ...orphanDocs.map(d => d.doc_id)
  ];
  for (let i = 0; i < toDelete.length; i++) {
    updateProgress(i, toDelete.length + 1, `Eliminando doc ${toDelete[i]}...`);
    const res = await RagApiClient.deleteDocument(toDelete[i]);
    if (res.ok) ok++;
  }

  // Paso 2: Limpiar vectores sueltos via cleanup-orphans (DELETE WHERE fileName IS NULL)
  if (orphanVectors > 0) {
    updateProgress(toDelete.length, toDelete.length + 1, `Limpiando ${orphanVectors} vectores sueltos...`);
    const cleanRes = await RagApiClient.cleanupOrphans();
    if (cleanRes.ok) ok++;
  }

  updateProgress(toDelete.length + 1, toDelete.length + 1, `Listo: ${ok} operaciones exitosas`);
  await loadAll(container);
  setTimeout(() => showProgress(false), 3000);
}

// ── View document ──

async function handleViewDocument(id, code, title) {
  const detail = await InstructionApiClient.getById(id);
  if (!detail.ok) {
    openDocumentModal(`${code} — ${title}`, `Error cargando: ${detail.error}`, { format: 'text' });
    return;
  }
  const data = detail.data?.data || detail.data;
  const body = buildInstructionContent(data);
  openDocumentModal(`${code} — ${title}`, body);
}

// ── Delete ──

async function handleDelete(docId, chunkCount, container) {
  if (!await confirmar({
    titulo: `Eliminar "${docId}" del vector store`,
    texto: `${chunkCount} chunk(s) seran borrados.`,
    aceptar: 'Eliminar',
    peligro: true
  })) return;

  const result = await RagApiClient.deleteDocument(docId);
  if (result.ok) {
    await loadAll(container);
  } else {
    avisar('Error eliminando: ' + (result.error || 'desconocido'), { tono: 'error' });
  }
}

// ── Ingest single ──

async function handleIngestSingle(id, code, title, container) {
  const btn = document.querySelector(`.kg-btn-action[data-code="${CSS.escape(code)}"]`);
  if (btn) { btn.disabled = true; btn.textContent = 'Procesando...'; }

  if (ragDocIds.has(code)) {
    await RagApiClient.deleteDocument(code);
  }

  const success = await ingestInstruction(id, code, title);

  if (btn) {
    btn.disabled = false;
    btn.textContent = success ? 'Listo' : 'Error - Reintentar';
  }

  if (success) await loadAll(container);
}

async function ingestInstruction(id, code, title) {
  const detail = await InstructionApiClient.getById(id);
  if (!detail.ok) return false;

  const data = detail.data?.data || detail.data;
  const body = buildInstructionContent(data);
  if (!body || body.length < 10) return false;

  const version = data.instructionVersion || '1.0.0';
  const content = `# ${code} - ${title}\n\nArea: ${data.area || 'general'}\nVersion: ${version}\n\n${body}`;

  const result = await RagApiClient.ingestText({
    doc_id: code,
    title: `${code} - ${title}`,
    content,
    collection: 'public',
    metadata: { code, area: data.area || '', version }
  });

  if (result.ok) {
    await RagApiClient.enrichMetadata(code, `${code} - ${title}`);

    // Track RAG version en BD (graceful — endpoint puede no existir aun)
    await InstructionApiClient.patchRagStatus(id, {
      ragVersion: version,
      ragIngestedAt: new Date().toISOString()
    });
    // ^ Retorna {ok:false} si el endpoint no existe; no bloquea el flujo
  }

  return result.ok;
}

// ── Ingest all ──

async function handleIngestAll(container) {
  const toIngest = instructions.filter(i => i.status === 'active');
  if (toIngest.length === 0) { avisar('No hay instrucciones activas'); return; }

  // Calcular limpieza pendiente
  const archivedWithRag = _archivedRows.filter(r => r.ragDoc);
  const instructionCodes = new Set(instructions.map(i => i.code));
  const orphanDocs = ragDocs.filter(d => !instructionCodes.has(d.doc_id));
  const cleanupCount = archivedWithRag.length + orphanDocs.length;

  let msg = 'Las existentes se re-ingestaran (delete + insert).';
  if (cleanupCount > 0) {
    msg += `\n\nAdemas se limpiaran:`;
    if (archivedWithRag.length > 0) msg += `\n• ${archivedWithRag.length} archivadas con vectores obsoletos`;
    if (orphanDocs.length > 0) msg += `\n• ${orphanDocs.length} documentos huerfanos`;
  }
  if (!await confirmar({
    titulo: `Ingestar ${toIngest.length} instrucciones activas`,
    texto: msg,
    aceptar: 'Ingestar',
    peligro: true
  })) return;

  showProgress(true);
  let ok = 0, fail = 0;
  const totalSteps = toIngest.length + cleanupCount + 1; // +1 for cleanup-orphans step

  // Paso 1: Re-ingestar activas
  for (let i = 0; i < toIngest.length; i++) {
    const inst = toIngest[i];
    updateProgress(i, totalSteps, `Ingesta: ${inst.code}`);

    if (ragDocIds.has(inst.code)) {
      await RagApiClient.deleteDocument(inst.code);
    }
    const success = await ingestInstruction(inst.id, inst.code, inst.title);
    if (success) ok++; else fail++;
  }

  // Paso 2: Limpiar docs de archivadas
  let cleaned = 0;
  const toClean = [
    ...archivedWithRag.map(r => r.code),
    ...orphanDocs.map(d => d.doc_id)
  ];
  for (let i = 0; i < toClean.length; i++) {
    updateProgress(toIngest.length + i, totalSteps, `Limpiando doc: ${toClean[i]}`);
    const res = await RagApiClient.deleteDocument(toClean[i]);
    if (res.ok) cleaned++;
  }

  // Paso 3: Limpiar vectores sueltos (sin metadata.fileName)
  updateProgress(totalSteps - 1, totalSteps, 'Limpiando vectores sueltos...');
  const cleanupRes = await RagApiClient.cleanupOrphans();
  const orphansDeleted = cleanupRes.ok ? (cleanupRes.data?.orphans_deleted || '?') : 0;

  const summary = `Ingesta: ${ok} ok${fail > 0 ? `, ${fail} errores` : ''}${cleaned > 0 ? ` · Docs limpiados: ${cleaned}` : ''} · Vectores sueltos: ${orphansDeleted}`;
  updateProgress(totalSteps, totalSteps, summary);
  await loadAll(container);
  setTimeout(() => showProgress(false), 4000);
}

function showProgress(visible) {
  const $el = document.getElementById('kg-progress');
  if ($el) $el.hidden = !visible;
}

function updateProgress(current, total, label) {
  const $fill = document.getElementById('kg-progress-fill');
  const $text = document.getElementById('kg-progress-text');
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  if ($fill) $fill.style.width = `${pct}%`;
  if ($text) $text.textContent = `${current}/${total} — ${label}`;
}
